///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import messages from "./messages";
import {
  controls,
  equipments,
  patterns,
  fscTypesIds,
  awardType,
  awardRank,
  cellUnit,
  rfqStatusTypes
} from "./types";
import { getShipperDistanceStyle } from "./getShipperDistanceStyle";
import euColumnConstants from "./euColumnConstants";
import {
  areNumbers,
  calculateBaseRpm,
  calculateCpm,
  calculateFlatRate,
  calculateRpm,
  calculateTotalCost,
  getShiperMiles,
  numberParse,
  round,
  roundForNullableField,
  numberParseForEmptyValue,
  formatDate
} from "./laneCalculations";

const {
  LANE_HAS_MATCHES,
  SHIPPER_LANE_ID,
  EDITED,
  TOTAL_RATE,
  RATE_PER_UNIT,
  FLAT_RATE,
  BASE_RATE_PER_UNIT,
  FUEL_RATE,
  NDI,
  ORIGIN_PLACE,
  ORIGIN_POSTAL_CODE,
  ORIGIN_COUNTRY,
  ORIGIN_CLUSTER_NAME,
  DESTINATION_PLACE,
  DESTINATION_POSTAL_CODE,
  DESTINATION_COUNTRY,
  DESTINATION_CLUSTER_NAME,
  EQUIPMENT_TYPE,
  SHIPPER_DISTANCE,
  COYOTE_DISTANCE,
  PREVIOUS_TTT_COST,
  TTT_COST,
  COST_PER_UNIT,
  TOTAL_COST,
  MARGIN,
  MARGIN_PERCENTAGE,
  REGION,
  VOLUME,
  ORIGIN_NOTE,
  DESTINATION_NOTE,
  LAST_EDITED_USER_NAME,
  DROP_TRAILER,
  HRHV,
  INCUMBENT_RATE,
  AWARD_RATE,
  AWARD_VOLUME,
  AWARD_TYPE,
  AWARD_RANK,
  BID_START_DATE,
  BID_END_DATE,
  SHIPPER_NAME,
  BID_NAME,
  BID_STATUS,
  SHIPPER_TRANSIT_TIME,
  DOUBLE_MAN,
  ADR,
  PALLET_EXCHANGE,
  NUMBER_OF_ADDITIONAL_STOPS,
  ROUND_TRIP,
  OPERATIONS_SURCHARGE_AMOUNT,
  IMPORT_LANE_ID
} = euColumnConstants;
const FUEL_SURCHARGE_TYPE = "fuelSurchargeType";
const FUEL_SURCHARGE = "";
export { MARGIN, MARGIN_PERCENTAGE };

export const euColumns = [
  {
    field: LANE_HAS_MATCHES,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: "",
    left: true,
    control: controls.laneMatch,
    resize: true,
    isDraggable: false,
    columnChooserLabel: messages.laneMatchCount.defaultMessage,
    requiredColumn: true
  },
  {
    field: IMPORT_LANE_ID,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.laneId.defaultMessage,
    left: true,
    control: controls.none,
    width: "medium",
    filter: true,
    requiredColumn: true
  },
  {
    field: SHIPPER_LANE_ID,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.shipperLaneId.defaultMessage,
    left: true,
    control: controls.none,
    width: "medium",
    filter: true
  },
  {
    field: EDITED,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.edited.defaultMessage,
    control: controls.tag,
    center: true,
    disabled: true,
    width: "tiny",
    laneMatchOrder: 16,
    tagLabel: [
      { value: true, label: messages.edited.defaultMessage },
      { value: false, label: "" }
    ]
  },
  {
    field: TOTAL_RATE,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.totRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) => {
      if ([FLAT_RATE, BASE_RATE_PER_UNIT].some(i => i === changedBy)) {
        if (areNumbers(lane, euColumnConstants, [FLAT_RATE, FUEL_RATE])) {
          lane[TOTAL_RATE] = lane[FLAT_RATE] + lane[FUEL_RATE];
        }
      } else if (changedBy === RATE_PER_UNIT) {
        if (areNumbers(lane, euColumnConstants, [RATE_PER_UNIT])) {
          lane[TOTAL_RATE] =
            lane[RATE_PER_UNIT] * getShiperMiles(lane, euColumnConstants);
        }
      } else {
        if (
          areNumbers(lane, euColumnConstants, [
            TOTAL_COST,
            MARGIN,
            OPERATIONS_SURCHARGE_AMOUNT
          ])
        ) {
          lane[TOTAL_RATE] =
            lane[TOTAL_COST] + lane[MARGIN] + lane[OPERATIONS_SURCHARGE_AMOUNT];
        }
      }
    },
    format: round,
    effects: lane =>
      lane[FUEL_SURCHARGE_TYPE] === fscTypesIds.percentOfRate
        ? [
            MARGIN,
            MARGIN_PERCENTAGE,
            FLAT_RATE,
            RATE_PER_UNIT,
            BASE_RATE_PER_UNIT,
            FUEL_SURCHARGE
          ]
        : [
            MARGIN,
            MARGIN_PERCENTAGE,
            FLAT_RATE,
            RATE_PER_UNIT,
            BASE_RATE_PER_UNIT
          ]
  },
  {
    field: RATE_PER_UNIT,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.rpKm.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "tiny",
    pattern: patterns.intTwoFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateRpm({ lane, changedBy, constants: euColumnConstants }),
    format: round,
    effects: () => [
      TOTAL_RATE,
      TOTAL_COST,
      MARGIN,
      MARGIN_PERCENTAGE,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: FLAT_RATE,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.flatRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateFlatRate({ lane, changedBy, constants: euColumnConstants }),
    format: round,
    effects: () => [
      TOTAL_RATE,
      MARGIN,
      MARGIN_PERCENTAGE,
      BASE_RATE_PER_UNIT,
      RATE_PER_UNIT,
      FUEL_RATE
    ]
  },
  {
    field: BASE_RATE_PER_UNIT,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.baseRpKm.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intTwoFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateBaseRpm({ lane, changedBy, constants: euColumnConstants }),
    format: round,
    effects: () => [
      FUEL_RATE,
      TOTAL_COST,
      FLAT_RATE,
      TOTAL_RATE,
      MARGIN,
      MARGIN_PERCENTAGE,
      RATE_PER_UNIT,
      FUEL_SURCHARGE
    ]
  },
  {
    field: FUEL_RATE,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.fuelRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    format: round,
    effects: () => [
      TOTAL_COST,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_SURCHARGE,
      MARGIN,
      MARGIN_PERCENTAGE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: NDI,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.tradeLanes.defaultMessage,
    control: controls.checkbox,
    center: true,
    disabled: true,
    width: "tiny"
  },
  {
    field: ORIGIN_PLACE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origPlace.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true,
    laneMatchOrder: 1
  },
  {
    field: ORIGIN_POSTAL_CODE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origPostal.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true
  },
  {
    field: ORIGIN_COUNTRY,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origCountry.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true
  },
  {
    field: ORIGIN_CLUSTER_NAME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origCluster.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true
  },
  {
    field: DESTINATION_PLACE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destPlace.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true,
    laneMatchOrder: 3
  },
  {
    field: DESTINATION_POSTAL_CODE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destPostal.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: DESTINATION_COUNTRY,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destCountry.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: DESTINATION_CLUSTER_NAME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destCluster.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: EQUIPMENT_TYPE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.eq.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true,
    format: value => equipments[value],
    laneMatchOrder: 11
  },
  {
    field: SHIPPER_DISTANCE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.shipKm.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intFourFractionalOne,
    parse: numberParseForEmptyValue,
    format: roundForNullableField,
    effects: () => [
      TOTAL_COST,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      MARGIN,
      MARGIN_PERCENTAGE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ],
    rangeFilter: true,
    style: lane =>
      getShipperDistanceStyle(lane[SHIPPER_DISTANCE], lane[COYOTE_DISTANCE])
  },
  {
    field: COYOTE_DISTANCE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.coyoteKm.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.minOneIntFourFractionalOne,
    parse: numberParse,
    format: round,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ],
    rangeFilter: true,
    laneMatchOrder: 5,
    alternateHeader: messages.km.defaultMessage
  },
  {
    field: PREVIOUS_TTT_COST,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.prevTTTCost.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true,
    format: roundForNullableField
  },
  {
    field: TTT_COST,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.tttCost.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true
  },
  {
    field: COST_PER_UNIT,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.cpKm.defaultMessage,
    right: true,
    width: "tiny",
    control: controls.textInput,
    pattern: patterns.intTwoFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateCpm({ lane, changedBy, constants: euColumnConstants }),
    format: round,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      FUEL_RATE,
      FLAT_RATE,
      RATE_PER_UNIT,
      BASE_RATE_PER_UNIT
    ],
    laneMatchOrder: 13
  },
  {
    field: TOTAL_COST,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.totCost.defaultMessage,
    right: true,
    width: "medium",
    control: controls.textInput,
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    calculate: (lane, changedBy) =>
      calculateTotalCost({ lane, changedBy, constants: euColumnConstants }),
    format: round,
    effects: () => [
      COST_PER_UNIT,
      MARGIN,
      TOTAL_RATE,
      FUEL_RATE,
      FLAT_RATE,
      RATE_PER_UNIT,
      BASE_RATE_PER_UNIT
    ],
    laneMatchOrder: 14
  },
  {
    field: MARGIN,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.margin.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    parse: numberParse,
    calculate: (lane, changedBy) => {
      if (
        changedBy === TOTAL_COST ||
        changedBy === COST_PER_UNIT ||
        changedBy === COYOTE_DISTANCE ||
        changedBy === OPERATIONS_SURCHARGE_AMOUNT ||
        changedBy === MARGIN_PERCENTAGE
      ) {
        if (
          areNumbers(lane, euColumnConstants, [
            TOTAL_COST,
            MARGIN_PERCENTAGE,
            OPERATIONS_SURCHARGE_AMOUNT
          ])
        ) {
          lane[MARGIN] =
            (lane[TOTAL_COST] + lane[OPERATIONS_SURCHARGE_AMOUNT]) /
              (1 - lane[MARGIN_PERCENTAGE] / 100) -
            (lane[TOTAL_COST] + lane[OPERATIONS_SURCHARGE_AMOUNT]);
        }
      } else {
        if (
          areNumbers(lane, euColumnConstants, [
            TOTAL_RATE,
            OPERATIONS_SURCHARGE_AMOUNT,
            TOTAL_COST
          ])
        ) {
          lane[MARGIN] =
            lane[TOTAL_RATE] -
            lane[TOTAL_COST] -
            lane[OPERATIONS_SURCHARGE_AMOUNT];
        }
      }
    },
    pattern: patterns.intSixFractionalTwoWithNegative,
    format: round,
    effects: () => [
      TOTAL_COST,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT,
      MARGIN_PERCENTAGE
    ],
    laneMatchOrder: 15
  },
  {
    field: MARGIN_PERCENTAGE,
    cellUnit: cellUnit.percent,
    unitLeft: false,
    header: messages.marginPct.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intTwoFractionalTwo,
    parse: numberParse,
    calculate: lane => {
      if (
        areNumbers(lane, euColumnConstants, [
          MARGIN,
          TOTAL_RATE,
          TOTAL_COST,
          OPERATIONS_SURCHARGE_AMOUNT
        ])
      ) {
        if (
          lane[OPERATIONS_SURCHARGE_AMOUNT] +
            lane[MARGIN] +
            lane[TOTAL_COST] ===
          0
        ) {
          lane[MARGIN_PERCENTAGE] = 0;
        } else {
          lane[MARGIN_PERCENTAGE] =
            (100 * lane[MARGIN]) /
            (lane[MARGIN] +
              lane[TOTAL_COST] +
              lane[OPERATIONS_SURCHARGE_AMOUNT]);
        }
      }
    },
    format: round,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },
  {
    field: REGION,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.region.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: VOLUME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.vol.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "tiny",
    pattern: patterns.positiveInteger,
    parse: numberParse,
    rangeFilter: true,
    laneMatchOrder: 12
  },
  {
    field: OPERATIONS_SURCHARGE_AMOUNT,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.opSurchargeAmt.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    format: round,
    effects: () => [
      TOTAL_COST,
      MARGIN,
      TOTAL_RATE,
      RATE_PER_UNIT,
      FUEL_RATE,
      FLAT_RATE,
      BASE_RATE_PER_UNIT
    ]
  },

  {
    field: ORIGIN_NOTE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.origNotes.defaultMessage,
    control: controls.textInput,
    width: "large",
    filter: true,
    left: true,
    pattern: patterns.maxThousandCharacters
  },
  {
    field: DESTINATION_NOTE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.destNotes.defaultMessage,
    control: controls.textInput,
    width: "large",
    filter: true,
    left: true,
    pattern: patterns.maxThousandCharacters
  },
  {
    field: LAST_EDITED_USER_NAME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.lastUpdate.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true
  },
  {
    field: DROP_TRAILER,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.dropTrailer.defaultMessage,
    control: controls.checkbox,
    center: true
  },
  {
    field: DOUBLE_MAN,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.doubleMan.defaultMessage,
    control: controls.checkbox,
    center: true
  },
  {
    field: ADR,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.adr.defaultMessage,
    control: controls.checkbox,
    center: true
  },
  {
    field: PALLET_EXCHANGE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.palletExchange.defaultMessage,
    control: controls.checkbox,
    center: true
  },
  {
    field: HRHV,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.hrhv.defaultMessage,
    control: controls.checkbox,
    width: "tiny",
    center: true
  },
  {
    field: ROUND_TRIP,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.roundTrip.defaultMessage,
    control: controls.checkbox,
    width: "tiny",
    center: true
  },
  {
    field: INCUMBENT_RATE,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.incmbtRate.defaultMessage,
    right: true,
    control: controls.textInput,
    width: "medium",
    pattern: patterns.intSixFractionalTwo,
    parse: numberParse,
    filter: true
  },
  {
    field: AWARD_RATE,
    cellUnit: cellUnit.euro,
    unitLeft: true,
    header: messages.awardRate.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true
  },
  {
    field: AWARD_VOLUME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.awardVol.defaultMessage,
    control: controls.none,
    width: "medium",
    right: true,
    rangeFilter: true
  },
  {
    field: AWARD_TYPE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.awardType.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: value =>
      !value ? value : awardType.find(f => f.value === value).label
  },
  {
    field: AWARD_RANK,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.awardRank.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: value =>
      !value ? value : awardRank.find(f => f.value === value).label
  },
  {
    field: SHIPPER_TRANSIT_TIME,
    cellUnit: cellUnit.none,
    header: messages.shipTransTime.defaultMessage,
    control: controls.none,
    width: "",
    filter: true,
    left: true
  },
  {
    field: NUMBER_OF_ADDITIONAL_STOPS,
    cellUnit: cellUnit.none,
    header: messages.numberOfAddStops.defaultMessage,
    control: controls.none,
    width: "",
    right: true,
    rangeFilter: true
  },
  {
    field: "id",
    header: "",
    control: controls.delete,
    isDraggable: false,
    columnChooserLabel: messages.delete.defaultMessage
  },
  {
    field: BID_START_DATE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bidStartDate.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: formatDate,
    laneMatchOrder: 6,
    onlyLaneMatch: true
  },
  {
    field: BID_END_DATE,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bidEndDate.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: formatDate,
    laneMatchOrder: 7,
    onlyLaneMatch: true
  },
  {
    field: SHIPPER_NAME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.shipper.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    laneMatchOrder: 8,
    onlyLaneMatch: true
  },
  {
    field: BID_NAME,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.bidName.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    laneMatchOrder: 9,
    onlyLaneMatch: true
  },
  {
    field: BID_STATUS,
    cellUnit: cellUnit.none,
    unitLeft: true,
    header: messages.status.defaultMessage,
    control: controls.none,
    width: "medium",
    filter: true,
    left: true,
    format: value => value && rfqStatusTypes.find(x => x.value === value).label,
    laneMatchOrder: 10,
    onlyLaneMatch: true
  }
];

export default euColumns;
